// Utility functions for authentication

/**
 * Sets authentication data in localStorage
 * @param {Object} data - Contains token and user data
 */
export const setAuth = (data) => {
  if (data.token) {
    localStorage.setItem('auth_token', data.token);
  }
  
  if (data.user) {
    localStorage.setItem('user', JSON.stringify(data.user));
  }
};

/**
 * Clears authentication data from localStorage
 */
export const clearAuth = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
};

/**
 * Gets the current user from localStorage
 * @return {Object|null} The user object or null if not logged in
 */
export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

/**
 * Gets the authentication token from localStorage
 * @return {string|null} The authentication token or null if not available
 */
export const getToken = () => {
  return localStorage.getItem('auth_token');
};

/**
 * Checks if the user is authenticated
 * @return {boolean} True if the user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Updates user data in localStorage
 * @param {Object} userData - Updated user data
 */
export const updateUser = (userData) => {
  const currentUser = getUser();
  if (currentUser) {
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }
};

/**
 * Checks if current user has the specified role
 * @param {string} role - Role to check
 * @return {boolean} True if user has the role, false otherwise
 */
export const hasRole = (role) => {
  const user = getUser();
  return user ? user.role === role : false;
};

/**
 * Checks if token is expired
 * @return {boolean} True if token is expired or invalid, false otherwise
 */
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true;
  
  try {
    // Get the payload part of the JWT
    const payload = token.split('.')[1];
    const decodedPayload = JSON.parse(atob(payload));
    
    // Check if the token is expired
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedPayload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // Assume token is expired if we can't decode it
  }
};
